import React, {useEffect, useState} from 'react';
import {css} from 'glamor';
import {useQuery, useSubscription} from "@apollo/client";
import * as theme from '../../theme';
import {withInstallation} from '../../components/InstallationContext';
import {FlashMessage} from '../../components/FlashMessage';
import {Button} from '../../components/Button';
import {Container, ColContainer, CardContainer} from '../../components/Container';
import {Left, Right} from '../../components/Header';
import {Well} from '../../components/Well';
import {H1, H2} from '../../components/Typography';
import {Table, THead, TBody} from '../../components/Table';
import {Loading, LoadingError} from '../../components/LoadingFork';
import {listSupportTicketQuery} from './queries';
import {DRIVO_URL} from "../../lib/drivoApollo";
import {StatusPill} from '../../components/StatusPill';
import authManager, {downloadWithAuth, PERMISSIONS} from "../../lib/authManager";
import gql from "graphql-tag";
import {fmtDate} from "../../lib/misc";
import SearchFilterPanel from './SearchFilterPanel';
import {PermissionOnly} from "../../components/AdminOnly";

/* ROWS PER PAGE OPTIONS FOR THE SUPPORT TICKET LIST */
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

/* DEFAULT FILTER VALUES FOR THE SUPPORT TICKET SEARCH PANEL */
const INITIAL_FILTERS = {
    searchTerm: '',
    supportType: '',
    status: '',
    dateFrom: '',
    dateTo: '',
};

function Header(props) {
    return <div
        className={css({
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'stretch',
        })}
        {...props}
    />
}

function Status({isOpen}) {
    return <td>
        <div
            className={css({
                display: 'flex',
                justifyContent: 'stretch',
                flexDirection: 'column',
            })}
        >
            <StatusPill flavour={isOpen ? 'success' : 'light'}>
                {isOpen ? 'Open' : 'Resolved'}
            </StatusPill>
        </div>
    </td>
}

const rowStyle = css({
    '> td': {
        verticalAlign: 'middle',
        height: 60,
        borderBottom: `1px solid ${theme.gray10}`,
        transition: 'all .2s linear',
    },
    '> td:first-child': {paddingLeft: 10},
    '> td:last-child': {paddingRight: 10},
    ':hover > td': {
        background: theme.gray10,
        cursor: 'pointer',
    },
});

/* PAGINATION BAR LAYOUT: RECORD COUNT ON LEFT, PAGE BUTTONS ON RIGHT */
const paginationStyle = css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 10px',
});

const paginationButtonsStyle = css({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
});

const pageButtonStyle = css({
    minWidth: 36,
    height: 36,
    border: `1px solid ${theme.gray20}`,
    borderRadius: 4,
    background: '#fff',
    cursor: 'pointer',
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
        background: theme.gray10,
    },
});

const activePageStyle = css({
    minWidth: 36,
    height: 36,
    border: 'none',
    borderRadius: 4,
    background: theme.flavours.primary.highlightColor,
    color: '#fff',
    cursor: 'default',
    fontSize: 14,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

/*
 * PAGINATION COMPONENT FOR SUPPORT TICKETS TABLE.
 * DISPLAYS "SHOWING X-Y OF Z TICKETS" ON THE LEFT AND PAGE NUMBER BUTTONS ON THE RIGHT.
 * SHOWS UP TO 7 PAGE NUMBERS WITH ELLIPSIS (...) FOR LARGE PAGE COUNTS.
 * WHEN FILTERS ARE ACTIVE, DISPLAYS "FILTERED" IN THE COUNT LABEL.
 */
function Pagination({page, totalPages, onPageChange, rangeStart, rangeEnd, totalCount, hasFilters, pageSize, onPageSizeChange}) {
    const pages = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
        pages.push(1);
        if (page > 3) pages.push('...');
        const start = Math.max(2, page - 1);
        const end = Math.min(totalPages - 1, page + 1);
        for (let i = start; i <= end; i++) pages.push(i);
        if (page < totalPages - 2) pages.push('...');
        pages.push(totalPages);
    }

    return (
        <div className={paginationStyle}>
            <div style={{fontSize: 14, color: '#666', display: 'flex', alignItems: 'center', gap: 16}}>
                <span>Showing {rangeStart}–{rangeEnd} of {totalCount} {hasFilters ? 'filtered ' : ''}tickets</span>
                <span style={{display: 'flex', alignItems: 'center', gap: 6}}>
                    Rows per page:
                    <select
                        aria-label="Rows per page"
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        style={{
                            border: `1px solid ${theme.gray20}`,
                            borderRadius: 4,
                            padding: '4px 8px',
                            fontSize: 14,
                            cursor: 'pointer',
                        }}
                    >
                        {PAGE_SIZE_OPTIONS.map(size => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </select>
                </span>
            </div>
            {totalPages > 1 && (
                <div className={paginationButtonsStyle}>
                    <button
                        className={pageButtonStyle}
                        onClick={() => onPageChange(page - 1)}
                        disabled={page === 1}
                        style={page === 1 ? {opacity: 0.4, cursor: 'default'} : {}}
                        aria-label="Previous page"
                    >
                        &laquo;
                    </button>
                    {pages.map((p, i) =>
                        p === '...'
                            ? <span key={`dots-${i}`} style={{padding: '0 4px'}}>...</span>
                            : <button
                                key={p}
                                className={p === page ? activePageStyle : pageButtonStyle}
                                onClick={() => p !== page && onPageChange(p)}
                                aria-label={`Go to page ${p}`}
                                aria-current={p === page ? 'page' : undefined}
                            >
                                {p}
                            </button>
                    )}
                    <button
                        className={pageButtonStyle}
                        onClick={() => onPageChange(page + 1)}
                        disabled={page === totalPages}
                        style={page === totalPages ? {opacity: 0.4, cursor: 'default'} : {}}
                        aria-label="Next page"
                    >
                        &raquo;
                    </button>
                </div>
            )}
        </div>
    );
}

function SupportTicketRow({installation, ticket, onClick}) {
    const {id, createdAt, createdBy, supportType} = ticket
    /*
     * UNREAD pill is only visible to RootAdmin users.
     * User roles: RootAdmin (isRootAdmin), Admin (isAdmin per installation), Member (base access).
     */
    let unread = authManager.isRootAdmin() && ticket?.status === "OPEN" && ticket?.ticketNotifications[0]?.readStatus === false

    const name = createdBy && createdBy.isRootAdmin ? "Drivo Team" : createdBy?.name
    return <tr key={id} className={rowStyle} onClick={onClick}>
        <td>{fmtDate(createdAt, installation.timeZoneName)} {' '} {unread && <StatusPill size='xsmall'>UNREAD</StatusPill>}</td>
        <td>{name}</td> 
        <td>{supportType}</td> 
        <Status isOpen={ticket.status === 'OPEN'}/>
    </tr>
}

function SupportTicketList({match, installation, location, history}) {
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    /* BUILD QUERY VARIABLES: PAGINATION + FILTERS PASSED TO SERVER-SIDE GRAPHQL QUERY */
    const queryVariables = {
        installationId: installation.id,
        userId: authManager.userId,
        page,
        pageSize,
        searchTerm: filters.searchTerm || undefined,
        supportType: filters.supportType || undefined,
        status: filters.status || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
    };

    const {data = {}, loading, error, refetch} = useQuery(listSupportTicketQuery, {
        fetchPolicy: 'no-cache',
        variables: queryVariables
    })

    const {data: notification} = useSubscription(gql`
        subscription notificationComment{
          notificationComment{
            createdById
          }
        }
    `, {
        fetchPolicy: 'no-cache',
        shouldResubscribe: true
    })
    useEffect(() => {
        if (!loading && notification && notification?.notificationComment) {
            if (notification?.notificationComment.createdById !== authManager.userId && notification?.notificationComment.createdById !== "") {
                refetch()
            }
        }
    }, [notification])

    const tickets = data.getAllSupportTicketByInstallationId || []
    const totalCount = data.supportTicketCount || 0
    const totalPages = Math.ceil(totalCount / pageSize)
    const hasFilters = filters.searchTerm || filters.supportType || filters.status || filters.dateFrom || filters.dateTo
    const rangeStart = totalCount > 0 ? ((page - 1) * pageSize) + 1 : 0
    const rangeEnd = Math.min(page * pageSize, totalCount)

    /* WHEN FILTERS CHANGE, RESET TO PAGE 1 SO USER SEES FIRST PAGE OF FILTERED RESULTS */
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setPage(1);
    };

    /* WHEN ROWS PER PAGE CHANGES, RESET TO PAGE 1 */
    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPage(1);
    };

    /* DOWNLOADS ALL TICKETS AS XLSX (NOT PAGINATED - FETCHES ALL FROM REST ENDPOINT) */
    const handleDownload = async (e) => {
        e.preventDefault();
        const filename = `support_tickets_${installation.brandingKey}.xlsx`;
        await downloadWithAuth({
            url: `${DRIVO_URL}/support-tickets/${installation.id}/download`,
            filename,
        });
    };

    return <div>
        <CardContainer>
            <Container size="large" spaceAbove="small" spaceBelow="small">
                <Header>
                    <Left>
                        <H1>Support Tickets</H1>
                    </Left>
                    <Right>
                        <ColContainer>
                            <PermissionOnly permissions={PERMISSIONS.REPORTS_EXPORT}>
                                {totalCount > 0 && (
                                    <Button onClick={handleDownload} flavour='dark'>
                                        Download
                                    </Button>
                                )}
                            </PermissionOnly>
                            <Button to={`${match.url}/new`} style={{'background-color': theme.flavours.primary.highlightColor}}>New ticket</Button>
                        </ColContainer>
                    </Right>
                </Header>
            </Container>

            <Container size="large" spaceBelow="small">
                <SearchFilterPanel
                    filters={filters}
                    onFilterChange={handleFilterChange}
                />
            </Container>

            <Container spaceBelow="large">
                <FlashMessage locationState={location.state}/>
                {loading ? <Loading/> : null}
                {error ? <LoadingError error={error}/> : null}
                {!loading && totalCount === 0 && <Well>
                    <div>
                        <H2 spaceBelow="large">No tickets available</H2>
                        <Button spaceBelow="none" to={`${match.url}/new`}>
                            New Ticket
                        </Button>
                    </div>
                </Well>}
                {!loading && tickets.length > 0 && <Table>
                    <THead>
                        <tr> 
                            <th>Created At</th> 
                            <th>Created By</th> 
                            <th>Support Type</th> 
                            <th>Status</th> 
                        </tr> 
                    </THead>
                    <TBody>{tickets.map((ticket) => {
                        return <SupportTicketRow key={ticket.id} ticket={ticket} installation={installation} onClick={() => {
                            history.push(`${match.url}/${ticket.id}`);
                        }}/>
                    })}</TBody>
                </Table>}
                {!loading && totalCount > 0 && (
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        rangeStart={rangeStart}
                        rangeEnd={rangeEnd}
                        totalCount={totalCount}
                        hasFilters={hasFilters}
                        pageSize={pageSize}
                        onPageSizeChange={handlePageSizeChange}
                    />
                )}
            </Container>
        </CardContainer>

    </div>
}

export default withInstallation(SupportTicketList)
